import { Component, ElementRef, Inject, OnDestroy, OnInit } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Observable } from 'rxjs';
import { ImageHoverPreviewService, ImageHoverViewerAnchor } from './image-hover-preview.service';

const POPUP_WIDTH_PX = 210;
const POPUP_HEIGHT_PX = 256;
const IMAGE_HEIGHT_PX = 160;
// Half the arrow's visual width (12 / 2). Used for horizontal centering.
const ARROW_HALF_WIDTH_PX = 6;
// Clamps so the arrow tip stays under the popup's rounded corners.
const ARROW_MIN_OFFSET_PX = 12;
const ARROW_MAX_OFFSET_PX = POPUP_WIDTH_PX - 22;
// Minimum gap between the popup and the viewport edges.
const VIEWPORT_MARGIN_PX = 10;
// Distance between the popup and its anchor element.
const ANCHOR_GAP_PX = 8;

// Re-export so existing consumers don't need to update their imports.
export { ImageHoverViewerAnchor } from './image-hover-preview.service';

@Component({
  selector: 'app-image-hover-viewer',
  templateUrl: './image-hover-viewer.component.html',
  styleUrls: ['./image-hover-viewer.component.scss']
})
export class ImageHoverViewerComponent implements OnInit, OnDestroy {

  readonly anchor$: Observable<ImageHoverViewerAnchor | null>;
  readonly imageBoxHeight = IMAGE_HEIGHT_PX;

  constructor(
    hoverService: ImageHoverPreviewService,
    private host: ElementRef<HTMLElement>,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.anchor$ = hoverService.anchor$;
  }

  /**
   * The popup is `position: fixed` with a high `z-index`, but z-index only competes inside the
   * nearest stacking context — and the hosts that embed this viewer sit inside a dialog, i.e. a
   * `.cdk-overlay-pane` (`z-index: 1000`), which is a stacking context of its own. Any pane opened
   * afterwards (the transaction product search panel) therefore paints over the popup no matter
   * what z-index it asks for. Reparenting to `<body>` puts the popup in the root stacking context,
   * where its 9999 clears the whole overlay container.
   */
  ngOnInit(): void {
    this.document.body.appendChild(this.host.nativeElement);
  }

  /**
   * Angular tears a view down by removing its root nodes; this host is no longer among them, so
   * it has to take itself out or it would outlive every dialog that mounted it.
   */
  ngOnDestroy(): void {
    this.host.nativeElement.remove();
  }

  showAbove(anchor: ImageHoverViewerAnchor): boolean {
    return anchor.rect.top - 12 >= POPUP_HEIGHT_PX;
  }

  top(anchor: ImageHoverViewerAnchor): number {
    return this.showAbove(anchor)
      ? anchor.rect.top - POPUP_HEIGHT_PX - ANCHOR_GAP_PX
      : anchor.rect.bottom + ANCHOR_GAP_PX;
  }

  left(anchor: ImageHoverViewerAnchor): number {
    const r = anchor.rect;
    const ideal = r.left + r.width / 2 - POPUP_WIDTH_PX / 2;
    return Math.max(
      VIEWPORT_MARGIN_PX,
      Math.min(ideal, window.innerWidth - POPUP_WIDTH_PX - VIEWPORT_MARGIN_PX)
    );
  }

  arrowLeft(anchor: ImageHoverViewerAnchor): number {
    const r = anchor.rect;
    const offset = r.left + r.width / 2 - this.left(anchor) - ARROW_HALF_WIDTH_PX;
    return Math.min(Math.max(offset, ARROW_MIN_OFFSET_PX), ARROW_MAX_OFFSET_PX);
  }
}
